<script setup lang="ts">
import { IOpenChoice, IOpenCustomization } from '~/lib/models/events'
import {
  OptionMain,
  OptionsGroupsMain,
  productInfoKey,
} from '~/lib/services/store/catalog/catalog.dtos'
import { OptionsGroupsType } from '~/lib/services/store/common.dto'

const props = defineProps<{
  optionGroup: OptionsGroupsMain
}>()

const emit = defineEmits<{
  (e: 'open-customization', payload: IOpenCustomization): void
  (e: 'open-choice', arg1: IOpenChoice): void
}>()

const productInfo = inject(productInfoKey)!
const { t } = useLocale()
const { translateInterpolated } = useTranslation()
const { trackCustomizeIngredients } = useGTM()

const excludedOptionsGroupsTypes = [
  OptionsGroupsType.CHOICE,
  OptionsGroupsType.SUGGESTIVE,
  OptionsGroupsType.COMMENT,
] as string[]

const isOptionSelected = (opt: OptionMain) => opt.qty === 1

const optionSelected = computed(() => {
  const optionWithQuantity = props.optionGroup.options.find(option => isOptionSelected(option))

  if (!optionWithQuantity?.optionsGroups.some(optGc => optGc.type === 'CHOICE'))
    return optionWithQuantity

  const optionChoices = optionWithQuantity?.optionsGroups.filter(optGc => optGc.type === 'CHOICE')

  const optionChoicesHasQuantity = optionChoices.every(optGc =>
    optGc.options.find(option => isOptionSelected(option))
  )

  if (optionChoicesHasQuantity) return optionWithQuantity

  return undefined
})

const optionNotSelectedText = computed(() => {
  const selectedMain = props.optionGroup.options.find(({ qty }) => qty === 1)
  if (selectedMain && selectedMain.optionsGroups.some(optGc => optGc.type === 'CHOICE'))
    return selectedMain.name
  return props.optionGroup.title
})
const customizationString = computed(() =>
  optionSelected.value
    ? translateInterpolated(
        getProductCustomizationResume(optionSelected.value?.optionsGroups || [])
      )
    : ''
)

const personalizationString = computed(() => {
  const textSeparator =
    thirdLevelOptionSelectedText.value.length && customizationString.value ? ' - ' : ''
  return !(thirdLevelOptionSelectedText.value.length || customizationString.value)
    ? ''
    : `${thirdLevelOptionSelectedText.value}${textSeparator}${customizationString.value}`
})

const changePersonalizationText = computed(() => {
  if (!optionSelected.value) return ''
  return t('productDetail.customization.actions.customize')
})

const thirdLevelOptionSelected = computed(() => {
  if (!optionSelected.value) return undefined

  const any3LevelMandatoryGrouped = optionSelected.value.optionsGroups.filter(
    optG => optG.type === OptionsGroupsType.CHOICE
  )

  if (
    !any3LevelMandatoryGrouped ||
    !any3LevelMandatoryGrouped.some(optGc => optGc.options.some(option => isOptionSelected(option)))
  )
    return undefined

  const selected3LevelMandatoryOptions = any3LevelMandatoryGrouped.map(
    optGc => optGc.options.find(option => isOptionSelected(option))?.name
  )

  return selected3LevelMandatoryOptions
})

// 1º Choice (McFlurry Oreo) 3º Choice (, Topping Caramelo)
const thirdLevelOptionSelectedText = computed(() => {
  if (thirdLevelOptionSelected.value) {
    return thirdLevelOptionSelected.value.join(', ')
  }
  return ''
})

// optionSelected.optionGroups || thirdLevelOptionSelected.optionsGroups have groups to customize
// beeing not CHOICE, COMMENT, SUGGESTIVE types
const choiceRowCustomizationGroupsAvailable = computed(() => {
  if (!optionSelected.value) return []
  const optionsGroupsToFilter = optionSelected.value.optionsGroups
  return optionsGroupsToFilter.filter(
    optG => !excludedOptionsGroupsTypes.includes(optG.type as string)
  )
})

const optionGroupHasMultipleOptions = computed(() => {
  if (!(props.optionGroup.options.length > 1)) {
    const option = props.optionGroup.options[0]

    return option.optionsGroups.some(optGc => optGc.type === 'CHOICE')
  }
  return true
})

const emitOpenCustomization = (payload: IOpenCustomization) => {
  const { identifier: id, name } = optionSelected.value!
  emit('open-customization', payload)
  trackCustomizeIngredients(productInfo, { id, name })
}
const emitOpenChoice = (payload: IOpenChoice) => emit('open-choice', payload)
</script>

<template>
  <div data-test="choice-row" class="mb-4 flex flex-row items-center gap-2">
    <!-- IsOptionSelected: choiceRow option with qty === 1 -->
    <template v-if="optionSelected">
      <ImageResolver
        class="h-auto w-[50px]"
        :url="optionSelected.imageUrl"
        :alt="`${optionSelected.name} image`"
      />
      <div class="flex flex-1 flex-col gap-2">
        <p class="text-sm font-bold" data-test="option-text">{{ optionSelected.name }}</p>

        <!-- Take into account when has 3 level option selected may not have optionsGroups length -->
        <p
          v-if="choiceRowCustomizationGroupsAvailable.length"
          class="cursor-pointer text-xs text-mcd-secondaryDarkGrey underline"
          @click="($ev: MouseEvent) => emitOpenCustomization({event: $ev as PointerEvent, optionsGroups: choiceRowCustomizationGroupsAvailable, openedComponent: {
            id: optionSelected?.identifier || '', 
            name: optionSelected?.name || '', 
          }})"
        >
          {{ changePersonalizationText }}
        </p>

        <!-- Personalization string -->
        <p
          v-if="personalizationString"
          v-dompurify-html="personalizationString"
          class="line-clamp-2 text-xs text-mcd-secondaryDarkGrey"
        ></p>
      </div>

      <McdButton
        v-if="optionGroupHasMultipleOptions"
        class="btn-customization h-fit self-start"
        @on-click="($ev: PointerEvent) => emitOpenChoice({event: $ev, optionGroup})"
      >
        {{ t('productDetail.customization.actions.change') }}
      </McdButton>
    </template>

    <!-- To select option -->
    <template v-else>
      <div class="flex-1">
        <p class="mb-2 text-sm font-bold" data-test="option-text">{{ optionNotSelectedText }}</p>

        <p class="text-xs text-mcd-secondaryDarkGrey">
          {{ t('productDetail.customization.chooseOne') }}
          <i>{{ t('productDetail.customization.mandatory') }}</i>
        </p>
      </div>

      <McdButton
        is-primary
        class="btn-customization h-fit self-start"
        @on-click="($ev: PointerEvent) => emitOpenChoice({event: $ev, optionGroup})"
      >
        {{ t('productDetail.customization.actions.select') }}
      </McdButton>
    </template>
  </div>
</template>
